import { useEffect, useMemo, useRef, useState } from 'react';
import { X, ImageIcon, ExternalLink } from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import type { AdCardData } from '../../pages/canvas/canvas-types';

interface DailyPoint {
  date: string;
  spend: number;
  roas: number;
  purchases: number;
}

function AdTrendChart({ daily }: { daily: DailyPoint[] }) {
  // Format MM-DD for x-axis tick labels (full ISO date is too cramped at this width)
  const data = useMemo(
    () =>
      daily.map((d) => ({
        ...d,
        // d.date might be ISO ("2026-04-15") or already YYYY-MM-DD
        label: d.date.slice(5),
      })),
    [daily],
  );
  if (data.length === 0) return null;
  return (
    <div data-testid="ad-detail-sidebar-chart" style={{ height: 220, marginTop: 12 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#F0EDE9" strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#8C8578' }} stroke="#E5E5E3" />
          <YAxis
            yAxisId="spend"
            orientation="left"
            tick={{ fontSize: 10, fill: '#8C8578' }}
            stroke="#E5E5E3"
            tickFormatter={(v) => `$${Math.round(v)}`}
          />
          <YAxis
            yAxisId="roas"
            orientation="right"
            tick={{ fontSize: 10, fill: '#8C8578' }}
            stroke="#E5E5E3"
            tickFormatter={(v) => `${v.toFixed(1)}x`}
          />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #E5E5E3', background: '#FFFFFF' }}
            formatter={(value: number, name: string) => {
              if (name === 'spend') return [`$${value.toFixed(2)}`, 'Spend'];
              if (name === 'roas') return [`${value.toFixed(2)}x`, 'ROAS'];
              if (name === 'purchases') return [String(value), 'Purchases'];
              return [value, name];
            }}
            labelFormatter={(label) => `Day ${label}`}
          />
          <Legend
            wrapperStyle={{ fontSize: 10, paddingTop: 4 }}
            iconSize={8}
          />
          <Bar yAxisId="spend" dataKey="spend" fill="#E5E2DC" name="Spend" radius={[2, 2, 0, 0]} />
          <Line yAxisId="roas" type="monotone" dataKey="roas" stroke="#1A1A1A" strokeWidth={2} dot={false} name="ROAS" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

interface KPITileProps {
  label: string;
  value: string;
}

function KPITile({ label, value }: KPITileProps) {
  return (
    <div
      data-testid={`ad-sidebar-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
      style={{
        background: '#F8F8F7',
        border: '1px solid #E5E5E3',
        borderRadius: 10,
        padding: '10px 12px',
      }}
    >
      <div style={{ fontSize: 10, color: '#B5B0A8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#1A1A1A' }}>{value}</div>
    </div>
  );
}

function fmt(v?: number): string {
  if (v === undefined || v === null) return '-';
  return v.toLocaleString();
}
function fmtCurrency(v?: number): string {
  if (v === undefined || v === null) return '-';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPct(v?: number): string {
  if (v === undefined || v === null) return '-';
  return `${v.toFixed(2)}%`;
}
function fmtRoas(v?: number): string {
  if (v === undefined || v === null) return '-';
  return `${v.toFixed(2)}x`;
}

function KpiBlock({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ padding: 10, background: '#F9FAFB', borderRadius: 8 }}>
      <div style={{ fontSize: 10, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: '#111827', marginTop: 2 }}>{value}</div>
    </div>
  );
}

const statusColors: Record<string, { bg: string; text: string }> = {
  ACTIVE: { bg: '#DCFCE7', text: '#166534' },
  PAUSED: { bg: '#FEF9C3', text: '#713F12' },
  INACTIVE: { bg: '#F3F4F6', text: '#6B7280' },
  DELETED: { bg: '#FEE2E2', text: '#991B1B' },
};

interface AdDetailSidebarProps {
  ad: AdCardData | null;
  onClose: () => void;
  onSendToCanvas?: (ad: AdCardData) => void;
  /** Brand id used by the Explain Performance feature. When omitted the
   *  button is hidden — same brand context that fetches insights. */
  brandId?: number;
}

type InsightsData = {
  hasData: boolean;
  source?: 'meta' | 'mock';
  spend?: number;
  impressions?: number;
  clicks?: number;
  purchases?: number;
  revenue?: number;
  roas?: number;
  ctr?: number;
  cpc?: number;
  daily?: DailyPoint[];
};

export function AdDetailSidebar({ ad, onClose, onSendToCanvas, brandId }: AdDetailSidebarProps) {
  const open = !!ad;
  const backdropRef = useRef<HTMLDivElement>(null);
  const [insights, setInsights] = useState<InsightsData | null>(null);
  // Explain-performance state (Phase 2). Stays per-ad — resets when ad changes.
  const [explanation, setExplanation] = useState<string | null>(null);
  const [explainLoading, setExplainLoading] = useState(false);
  const [explainError, setExplainError] = useState<string | null>(null);

  useEffect(() => {
    // Reset explanation when ad changes (avoids showing stale text on switch)
    setExplanation(null);
    setExplainError(null);
    setExplainLoading(false);
  }, [ad?.id]);

  const handleExplain = async () => {
    if (!ad || !brandId) return;
    setExplainLoading(true);
    setExplainError(null);
    setExplanation(null);
    try {
      const resp = await fetch('/api/analytics/explain-ad-performance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ brandId, adName: ad.name ?? ad.id }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setExplainError(data.error || `Request failed (${resp.status})`);
        return;
      }
      setExplanation(data.explanation || '(empty)');
    } catch (err) {
      setExplainError((err as Error).message || 'Request failed');
    } finally {
      setExplainLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    if (!ad?.id) return;
    setInsights(null);
    fetch(`/api/meta/insights/ad/${ad.id}`, { credentials: 'include' })
      .then((r) => r.json())
      .then(setInsights)
      .catch(() => setInsights({ hasData: false }));
  }, [ad?.id]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === backdropRef.current) onClose();
  };

  const isVideo = (url: string) => /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url);

  const statusKey = ad?.status ? ad.status.toUpperCase() : '';
  const statusStyle = statusColors[statusKey] || { bg: '#F5F3EE', text: '#8C8578' };

  return (
    <>
      {/* Backdrop */}
      <div
        ref={backdropRef}
        data-testid="ad-detail-sidebar-backdrop"
        onClick={handleBackdropClick}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.25)',
          zIndex: 200,
          pointerEvents: open ? 'auto' : 'none',
          opacity: open ? 1 : 0,
          transition: 'opacity 0.25s ease',
        }}
      />

      {/* Slide-in panel */}
      <div
        data-testid="ad-detail-sidebar"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '50%',
          minWidth: 340,
          maxWidth: 760,
          background: '#FFFFFF',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          zIndex: 201,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.32, 0, 0.15, 1)',
        }}
      >
        {/* Header */}
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 18px',
            borderBottom: '1px solid #E5E5E3',
            background: '#FFFFFF',
          }}
        >
          <ExternalLink size={15} style={{ color: '#8C8578', flexShrink: 0 }} />
          <span
            data-testid="ad-detail-sidebar-title"
            style={{
              flex: 1,
              fontWeight: 600,
              fontSize: 14,
              color: '#111827',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {ad?.name || 'Ad Detail'}
          </span>
          <button
            data-testid="ad-detail-sidebar-close"
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: '1px solid #E5E5E3',
              background: '#FFFFFF',
              cursor: 'pointer',
              flexShrink: 0,
              color: '#6B7280',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#FFFFFF'; }}
          >
            <X size={13} />
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {ad && (
            <>
              {/* Status badge */}
              {ad.status && (
                <div style={{ marginBottom: 14 }}>
                  <span
                    data-testid="ad-detail-sidebar-status"
                    style={{
                      display: 'inline-block',
                      padding: '3px 10px',
                      borderRadius: 20,
                      fontSize: 11,
                      fontWeight: 600,
                      background: statusStyle.bg,
                      color: statusStyle.text,
                      letterSpacing: '0.03em',
                    }}
                  >
                    {ad.status}
                  </span>
                </div>
              )}

              {/* Campaign / ad set meta */}
              {(ad.campaignName || ad.adSetName) && (
                <div style={{ marginBottom: 16, fontSize: 12, color: '#8C8578', lineHeight: 1.6 }}>
                  {ad.campaignName && (
                    <div data-testid="ad-detail-sidebar-campaign">
                      <span style={{ fontWeight: 600, color: '#6B6760' }}>Campaign:</span> {ad.campaignName}
                    </div>
                  )}
                  {ad.adSetName && (
                    <div data-testid="ad-detail-sidebar-adset">
                      <span style={{ fontWeight: 600, color: '#6B6760' }}>Ad Set:</span> {ad.adSetName}
                    </div>
                  )}
                </div>
              )}

              {/* Creative */}
              {(ad.videoUrl || ad.thumbnail_url) ? (
                <div style={{ marginBottom: 20, borderRadius: 12, overflow: 'hidden', border: '1px solid #E5E5E3' }}>
                  {ad.videoUrl && isVideo(ad.videoUrl) ? (
                    <video
                      src={ad.videoUrl}
                      poster={ad.thumbnail_url || undefined}
                      controls
                      data-testid="ad-detail-sidebar-creative-video"
                      style={{ width: '100%', maxHeight: 360, display: 'block', background: '#000' }}
                    />
                  ) : (
                    <img
                      src={ad.videoUrl || ad.thumbnail_url}
                      alt={ad.name}
                      data-testid="ad-detail-sidebar-creative"
                      style={{ width: '100%', maxHeight: 360, objectFit: 'cover', display: 'block' }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                </div>
              ) : (
                <div
                  style={{
                    marginBottom: 20,
                    borderRadius: 12,
                    border: '1px solid #E5E5E3',
                    height: 180,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#F8F8F7',
                  }}
                >
                  <div style={{ textAlign: 'center', color: '#B5B0A8' }}>
                    <ImageIcon style={{ width: 32, height: 32, marginBottom: 8 }} />
                    <div style={{ fontSize: 12 }}>No creative available</div>
                  </div>
                </div>
              )}

              {/* Ad Copy */}
              {(ad.headline || ad.adCopy || ad.cta) && (
                <div
                  style={{
                    background: '#F8F8F7',
                    border: '1px solid #E5E5E3',
                    borderRadius: 12,
                    padding: '14px 16px',
                    marginBottom: 20,
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#8C8578', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                    Ad Copy
                  </div>
                  {ad.headline && (
                    <div
                      data-testid="ad-detail-sidebar-headline"
                      style={{ fontSize: 15, fontWeight: 700, color: '#1A1A1A', marginBottom: ad.adCopy ? 8 : 0 }}
                    >
                      {ad.headline}
                    </div>
                  )}
                  {ad.adCopy && (
                    <div
                      data-testid="ad-detail-sidebar-copy"
                      style={{ fontSize: 13, color: '#3D3A36', lineHeight: 1.65, marginBottom: ad.cta ? 10 : 0 }}
                    >
                      {ad.adCopy}
                    </div>
                  )}
                  {ad.cta && (
                    <div
                      data-testid="ad-detail-sidebar-cta"
                      style={{
                        display: 'inline-block',
                        marginTop: 4,
                        padding: '5px 14px',
                        borderRadius: 6,
                        background: '#1A1A1A',
                        color: '#FFFFFF',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {ad.cta}
                    </div>
                  )}
                </div>
              )}

              {/* KPI Grid — lazy-fetched from meta_insights */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#8C8578', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                  Performance (last 30d)
                </div>
                {insights === null && (
                  <div data-testid="ad-detail-sidebar-insights-loading" style={{ fontSize: 12, color: '#9CA3AF' }}>Loading performance…</div>
                )}
                {insights && !insights.hasData && (
                  <div data-testid="ad-detail-sidebar-insights-empty" style={{ fontSize: 12, color: '#9CA3AF' }}>No performance data yet — this ad hasn't accrued insights.</div>
                )}
                {insights && insights.hasData && (
                  <>
                    <div
                      data-testid="ad-detail-sidebar-kpi-grid"
                      style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}
                    >
                      <KpiBlock label="Spend"     value={`$${insights.spend!.toFixed(2)}`} />
                      <KpiBlock label="ROAS"      value={`${insights.roas!.toFixed(2)}x`} />
                      <KpiBlock label="CTR"       value={`${insights.ctr!.toFixed(2)}%`} />
                      <KpiBlock label="CPC"       value={`$${insights.cpc!.toFixed(2)}`} />
                      <KpiBlock label="Purchases" value={String(insights.purchases)} />
                      <KpiBlock label="Revenue"   value={`$${insights.revenue!.toFixed(2)}`} />
                    </div>
                    {/* Daily trend chart — Spend bars + ROAS line, dual-axis */}
                    {insights.daily && insights.daily.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ fontSize: 10, color: '#8C8578', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                          Daily trend
                        </div>
                        <AdTrendChart daily={insights.daily} />
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* ── Explain Performance — sharp media-buyer answer ──
                  Hidden when no brandId in scope (caller didn't pass one). */}
              {brandId && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#8C8578', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Why is this performing this way?
                    </div>
                    {!explanation && !explainLoading && (
                      <button
                        data-testid="ad-detail-sidebar-explain"
                        onClick={handleExplain}
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '6px 10px',
                          borderRadius: 8,
                          background: '#1A1A1A',
                          color: '#FFFFFF',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        Explain →
                      </button>
                    )}
                  </div>
                  {explainLoading && (
                    <div style={{ fontSize: 12, color: '#9CA3AF', padding: '12px 0' }}>
                      Reading the data…
                    </div>
                  )}
                  {explainError && (
                    <div style={{ fontSize: 12, color: '#DC2626', padding: 10, background: '#FEF2F2', borderRadius: 8 }}>
                      {explainError}
                    </div>
                  )}
                  {explanation && (
                    <div
                      data-testid="ad-detail-sidebar-explanation"
                      style={{
                        fontSize: 13,
                        lineHeight: 1.55,
                        color: '#1A1A1A',
                        background: '#F8F8F7',
                        border: '1px solid #E5E5E3',
                        borderRadius: 10,
                        padding: 14,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {explanation}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer: Send to Canvas */}
        {ad && onSendToCanvas && (
          <div style={{ flexShrink: 0, padding: '14px 24px', borderTop: '1px solid #E5E5E3', background: '#FFFFFF' }}>
            <button
              data-testid="ad-detail-sidebar-send-to-canvas"
              onClick={() => onSendToCanvas(ad)}
              style={{
                width: '100%',
                padding: '10px 0',
                borderRadius: 10,
                background: '#1A1A1A',
                color: '#FFFFFF',
                fontSize: 13,
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#333333'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#1A1A1A'; }}
            >
              Send to Canvas
            </button>
          </div>
        )}
      </div>
    </>
  );
}
